import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { CheckSquare, Square } from "lucide-react";

const AdminEditModal = ({ show, onHide, onSave, admin, vendor }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    password: "",
    role: "admin",
    isActive: true,
    permissions: {},
  });

  const permissionGroups = {
    "Student Module": [
      { value: 'student_view', label: 'View Students' },
      { value: 'student_add', label: 'Add Student' },
      { value: 'student_edit', label: 'Edit Student' },
      { value: 'student_delete', label: 'Delete Student' },
      { value: 'student_export', label: 'Export Students' },
      { value: 'student_import', label: 'Import Students (Bulk)' },
    ],
    "Product Module": [
      { value: 'product_view', label: 'View Products' },
      { value: 'product_add', label: 'Add Product' },
      { value: 'product_edit', label: 'Edit Product' },
      { value: 'product_delete', label: 'Delete Product' },
      { value: 'product_export', label: 'Export Products' },
      { value: 'product_bulk_update', label: 'Bulk Update (CSV)' },
    ],
    "Bundle Module": [
      { value: 'bundle_view', label: 'View Bundles' },
      { value: 'bundle_add', label: 'Add Bundle' },
      { value: 'bundle_edit', label: 'Edit Bundle' },
      { value: 'bundle_sync', label: 'Sync Bundle' },
      { value: 'bundle_delete', label: 'Delete Bundle' },
      { value: 'bundle_details_view', label: 'View Bundle Details' },
    ],
    "Order Module": [
      { value: 'order_view', label: 'View Orders' },
      { value: 'order_details_view', label: 'View Order Details' },
      { value: 'order_edit', label: 'Edit Order (Manage Delivery)' },
      { value: 'order_status_update', label: 'Update Order Status' },
      { value: 'order_all_print', label: 'Print Order Slips' },
      { value: 'print_order_payment_slips', label: 'Print Payment Slip' },
      { value: 'order_export', label: 'Export Orders' },
      { value: 'order_bulk_upload', label: 'Bulk Upload Orders' },
      { value: 'dtdc_shipments_view', label: 'View DTDC Shipments & Invoices' },
    ],
    "Reports Module": [
      { value: 'reports_view', label: 'View General Sales Report' },
      { value: 'reports_product_wise_view', label: 'View Product-Wise Sales Report' },
      { value: 'reports_product_size_wise_view', label: 'View Product & Size-Wise Sales Report' },
      { value: 'reports_class_wise_view', label: 'View Product, Class & Size-Wise Sales Report' },
    ],
    "Admin Module": [
      { value: 'admin_view', label: 'View Admins' },
      { value: 'admin_add', label: 'Add Admin' },
      { value: 'admin_edit', label: 'Edit Admin' },
      { value: 'admin_delete', label: 'Delete Admin' },
    ],
    "Cart Module": [
      { value: 'cart_management_view', label: 'View User Carts' },
      { value: 'cart_edit', label: 'Edit Cart (Sizes & Quantities / Add Items)' },
      { value: 'cart_delete', label: 'Remove Cart Items / Clear Cart' },
    ]
  };

  useEffect(() => {
    if (admin) {
      setFormData({
        name: admin.name || "",
        email: admin.email || "",
        phoneNumber: admin.phoneNumber || "",
        password: "", // Don't populate password
        role: admin.role || (vendor ? "vendor" : "admin"),
        isActive: admin.isActive !== undefined ? admin.isActive : true,
        permissions: admin.permissions || {},
      });
    } else {
      setFormData({
        name: "",
        email: "",
        phoneNumber: "",
        password: "",
        role: vendor ? "vendor" : "admin",
        isActive: true,
        permissions: {},
      });
    }
  }, [admin, vendor, show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePermissionChange = (permValue) => {
    setFormData((prev) => {
      const newPermissions = { ...prev.permissions };
      if (newPermissions[permValue]) {
        delete newPermissions[permValue];
      } else {
        newPermissions[permValue] = true;
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleModuleToggle = (moduleName, options) => {
    setFormData((prev) => {
      const newPermissions = { ...prev.permissions };
      const allSelected = options.every(opt => prev.permissions[opt.value]);
      
      options.forEach(opt => {
        if (allSelected) {
          delete newPermissions[opt.value];
        } else {
          newPermissions[opt.value] = true;
        }
      });
      
      return { ...prev, permissions: newPermissions };
    });
  };

  const isModuleSelected = (options) => {
    return options.every(opt => formData.permissions[opt.value]);
  };

  const isModulePartiallySelected = (options) => {
    const selectedCount = options.filter(opt => formData.permissions[opt.value]).length;
    return selectedCount > 0 && selectedCount < options.length;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold text-primary">
          {admin ? "Edit Member" : "Add Member"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold">Name</Form.Label>
                <Form.Control
                  required
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold">Email</Form.Label>
                <Form.Control
                  required
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold">Phone Number</Form.Label>
                <Form.Control
                  required
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold">Password {admin && "(Leave blank to keep current)"}</Form.Label>
                <Form.Control
                  required={!admin}
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold">Role</Form.Label>
                <Form.Select name="role" value={formData.role} onChange={handleChange}>
                  {vendor ? (
                    <option value="vendor">Vendor</option>
                  ) : (
                    <>
                      <option value="admin">Admin</option>
                      <option value="vendor">Vendor</option>
                      <option value="stores">Stores</option>
                      <option value="accounts">Accounts</option>
                    </>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold">Status</Form.Label>
                <Form.Select name="isActive" value={formData.isActive} onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <hr className="my-4" />

          <h6 className="text-uppercase text-muted fw-bold mb-3 small border-bottom pb-2">Permissions</h6>
          <div className="permissions-container pr-2" style={{ maxHeight: '450px', overflowY: 'auto', overflowX: 'hidden' }}>
            <Row className="g-4">
              {Object.entries(permissionGroups).map(([groupName, options]) => (
                <Col md={12} key={groupName}>
                  <div className="border rounded p-3 bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                      <h6 className="text-primary mb-0">{groupName}</h6>
                      <Form.Check 
                        type="checkbox"
                        label="Select All"
                        className="small fw-bold"
                        checked={isModuleSelected(options)}
                        ref={el => el && (el.indeterminate = isModulePartiallySelected(options))}
                        onChange={() => handleModuleToggle(groupName, options)}
                      />
                    </div>
                    <Row className="g-2">
                      {options.map((option) => (
                        <Col key={option.value} md={4} sm={6}>
                          <div 
                            className="form-check cursor-pointer d-flex align-items-center"
                            onClick={() => handlePermissionChange(option.value)}
                          >
                            {formData.permissions[option.value] ? (
                              <CheckSquare size={18} className="text-primary me-2" />
                            ) : (
                              <Square size={18} className="text-muted me-2" />
                            )}
                            <span className="small">{option.label}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Save Member
          </Button>
        </Modal.Footer>
      </Form>
      <style>{`
        .cursor-pointer { cursor: pointer; }
        .permissions-container::-webkit-scrollbar { width: 6px; }
        .permissions-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        .permissions-container::-webkit-scrollbar-thumb { background: #ccc; border-radius: 10px; }
        .permissions-container::-webkit-scrollbar-thumb:hover { background: #bbb; }
      `}</style>
    </Modal>
  );
};

export default AdminEditModal;
